// src/services/azureRatesService.js
// Azure Table Storage Service for project rates (Ansätze)
// Stores hourly rates per project / person / phase

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projectRates",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const baseUrl = () =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;

// Ensure the table exists (create if not)
let tableEnsured = false;
const ensureTable = async () => {
  if (tableEnsured) return;
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/Tables?${AZURE_CONFIG.sasToken}`;
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json;odata=nometadata" },
      body: JSON.stringify({ TableName: AZURE_CONFIG.tableName }),
    });
  } catch {
    // Table might already exist - ignore
  }
  tableEnsured = true;
};

// RowKey: {projectCode}_{employeeId}_{phaseId}
// phaseId = "default" for a project-level default rate
const createRowKey = (projectCode, employeeId, phaseId = "default") =>
  `${projectCode}_${employeeId}_${phaseId}`;

/**
 * Load all rates for a project
 * @returns {Array<{projectCode, employeeId, phaseId, rate, updatedAt}>}
 */
export const getProjectRates = async (projectCode) => {
  await ensureTable();
  try {
    let all = [];
    let nextPK = null;
    let nextRK = null;
    const filter = `$filter=ProjectCode eq '${projectCode}'`;

    do {
      let url = `${baseUrl()}?${AZURE_CONFIG.sasToken}&${filter}`;
      if (nextPK && nextRK) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPK)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRK)}`;
      }

      const res = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!res.ok) throw new Error(`Azure Table Error: ${res.status}`);

      const data = await res.json();
      all = all.concat(data.value || []);

      nextPK = res.headers.get("x-ms-continuation-NextPartitionKey");
      nextRK = res.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPK && nextRK);

    return all.map((item) => ({
      projectCode: item.ProjectCode,
      employeeId: item.EmployeeId,
      phaseId: item.PhaseId || "default",
      rate: item.Rate || 0,
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));
  } catch (error) {
    console.error("getProjectRates Error:", error);
    return [];
  }
};

/**
 * Save a single rate entry (upsert)
 */
export const saveRate = async (projectCode, employeeId, phaseId, rate) => {
  await ensureTable();
  const rowKey = createRowKey(projectCode, employeeId, phaseId);
  const now = new Date().toISOString();

  const url = `${baseUrl()}(PartitionKey='rate',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

  const entity = {
    PartitionKey: "rate",
    RowKey: rowKey,
    ProjectCode: projectCode,
    EmployeeId: employeeId,
    PhaseId: phaseId,
    Rate: parseFloat(rate) || 0,
    CreatedAt: now,
    UpdatedAt: now,
  };

  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Azure Save Error: ${res.status} - ${errorText}`);
  }

  return entity;
};

/**
 * Save multiple rates at once
 */
export const saveRatesBatch = async (projectCode, rates) => {
  const promises = rates.map((r) =>
    saveRate(projectCode, r.employeeId, r.phaseId, r.rate)
  );
  return Promise.all(promises);
};

/**
 * Delete a single rate entry
 */
export const deleteRate = async (projectCode, employeeId, phaseId) => {
  await ensureTable();
  const rowKey = createRowKey(projectCode, employeeId, phaseId);
  const url = `${baseUrl()}(PartitionKey='rate',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

  const res = await fetch(url, {
    method: "DELETE",
    headers: { "If-Match": "*" },
  });

  if (!res.ok && res.status !== 404) {
    throw new Error(`Azure Delete Error: ${res.status}`);
  }
  return true;
};

/**
 * Delete all rates for a person in a project
 */
export const deletePersonRates = async (projectCode, employeeId) => {
  const allRates = await getProjectRates(projectCode);
  const personRates = allRates.filter((r) => r.employeeId === employeeId);
  const promises = personRates.map((r) =>
    deleteRate(projectCode, r.employeeId, r.phaseId)
  );
  return Promise.all(promises);
};

/**
 * Load rates for ALL projects (for dashboard use)
 */
export const getAllRates = async () => {
  await ensureTable();
  try {
    let all = [];
    let nextPK = null;
    let nextRK = null;

    do {
      let url = `${baseUrl()}?${AZURE_CONFIG.sasToken}`;
      if (nextPK && nextRK) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPK)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRK)}`;
      }

      const res = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!res.ok) throw new Error(`Azure Table Error: ${res.status}`);

      const data = await res.json();
      all = all.concat(data.value || []);

      nextPK = res.headers.get("x-ms-continuation-NextPartitionKey");
      nextRK = res.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPK && nextRK);

    return all.map((item) => ({
      projectCode: item.ProjectCode,
      employeeId: item.EmployeeId,
      phaseId: item.PhaseId || "default",
      rate: item.Rate || 0,
    }));
  } catch (error) {
    console.error("getAllRates Error:", error);
    return [];
  }
};

export const updateConfig = (newConfig) => {
  Object.assign(AZURE_CONFIG, newConfig);
};
